import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate, formatCurrency } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SavedFiltersDropdown,
  TableSettingsDropdown,
} from '../components/ui/enhanced-table';
import { useTableSort, useSavedFilters, useTableSettings } from '../hooks/useTableEnhancements';
import { Calendar, Plus, Pencil, Trash2, CheckCircle2, Clock, Settings2, List, BarChart3, X, Search } from 'lucide-react';
import { toast } from 'sonner';

const PM_RUN_STATUSES = ['scheduled', 'generated', 'skipped', 'failed'];

function ChecklistBuilder({ items, onChange }) {
  const { t } = useTranslation();
  
  const addItem = () => {
    const newItem = {
      id: `item-${Date.now()}`,
      text: '',
      is_required: false,
    };
    onChange([...items, newItem]);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('pm.checklistItems')}</Label>
        <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-checklist-item-btn">
          <Plus className="w-3.5 h-3.5 me-1" /> {t('pm.addItem')}
        </Button>
      </div>
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground py-2 text-center">{t('pm.noChecklistItems')}</p>
      )}
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={item.id} className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border-border">
            <div className="flex-1 space-y-2">
              <Input
                value={item.text}
                onChange={(e) => updateItem(idx, 'text', e.target.value)}
                placeholder={t('pm.checklistItemPlaceholder')}
                className="text-sm"
                data-testid={`checklist-item-${idx}-text`}
              />
              <div className="flex items-center gap-2">
                <Switch
                  checked={item.is_required}
                  onCheckedChange={(checked) => updateItem(idx, 'is_required', checked)}
                  id={`req-${item.id}`}
                />
                <Label htmlFor={`req-${item.id}`} className="text-xs text-muted-foreground cursor-pointer">
                  {t('common.required')}
                </Label>
              </div>
            </div>
            <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-red-500">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PMPlansPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [runs, setRuns] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [branches, setBranches] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [filterTargetType, setFilterTargetType] = useState('all');
  const [filterRunStatus, setFilterRunStatus] = useState('all');
  const [fSearch, setFSearch] = useState('');

  // Sorting with persistence per tab
  const plansSort = useTableSort('pm_plans', 'updated_at', 'desc');
  const runsSort = useTableSort('pm_runs', 'scheduled_for', 'desc');

  // Saved filters
  const { savedFilters, saveCurrentFilters, saveFilterPreset, deleteFilterPreset, loadCurrentFilters } = useSavedFilters('pm_page');

  // Column visibility for Plans tab
  const plansColumns = [
    { key: 'name', label: t('pm.planName') },
    { key: 'branch', label: t('common.branch') },
    { key: 'target', label: t('pm.targetType') },
    { key: 'frequency', label: t('pm.frequency') },
    { key: 'nextDue', label: t('pm.nextDue') },
    { key: 'status', label: t('common.status') },
    { key: 'updatedAt', label: t('common.updatedAt') },
    { key: 'actions', label: t('common.actions') },
  ];
  const plansSettings = useTableSettings('pm_plans_cols', plansColumns, ['name', 'branch', 'status']);

  // Column visibility for Runs tab
  const runsColumns = [
    { key: 'planName', label: t('pm.planName') },
    { key: 'branch', label: t('common.branch') },
    { key: 'scheduledFor', label: t('pm.scheduledFor') },
    { key: 'status', label: t('common.status') },
    { key: 'workOrder', label: t('workOrder.title') },
    { key: 'woStatus', label: t('pm.workOrderStatus') },
  ];
  const runsSettings = useTableSettings('pm_runs_cols', runsColumns, ['scheduledFor', 'status', 'branch']);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    branch_id: '',
    name: '',
    target_type: 'room',
    room_id: '',
    asset_id: '',
    frequency_type: 'weekly',
    interval: 1,
    cron_expression: '',
    checklist_items: [],
    is_active: true,
  });

  // Load saved filters on mount
  useEffect(() => {
    const saved = loadCurrentFilters();
    if (saved) {
      if (saved.branch) setFilterBranch(saved.branch);
      if (saved.active) setFilterActive(saved.active);
      if (saved.targetType) setFilterTargetType(saved.targetType);
      if (saved.runStatus) setFilterRunStatus(saved.runStatus);
      if (saved.tab) setActiveTab(saved.tab);
    }
  }, []);

  // Save current filters when they change
  useEffect(() => {
    saveCurrentFilters({ 
      branch: filterBranch, 
      active: filterActive, 
      targetType: filterTargetType, 
      runStatus: filterRunStatus,
      tab: activeTab 
    });
  }, [filterBranch, filterActive, filterTargetType, filterRunStatus, activeTab, saveCurrentFilters]);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterBranch !== 'all') params.branch_id = filterBranch;
      if (filterActive !== 'all') params.is_active = filterActive === 'active';
      if (filterTargetType !== 'all') params.target_type = filterTargetType;

      const [plansRes, runsRes, branchesRes, compRes] = await Promise.all([
        api.get('/pm/plans', { params }),
        api.get('/pm/runs'),
        api.get('/branches'),
        api.get('/pm/compliance'),
      ]);

      setPlans(plansRes.data);
      setRuns(runsRes.data);
      setBranches(branchesRes.data);
      setCompliance(compRes.data);
    } catch (err) {
      toast.error(t('toast.error'));
    } finally {
      setLoading(false);
    }
  }, [filterBranch, filterActive, filterTargetType, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadRoomsAssets = async (branchId) => {
    if (!branchId) return;
    try {
      const [roomsRes, assetsRes] = await Promise.all([
        api.get('/rooms', { params: { branch_id: branchId } }),
        api.get('/assets', { params: { branch_id: branchId } }),
      ]);
      setRooms(roomsRes.data);
      setAssets(assetsRes.data);
    } catch (err) {
      console.error('Failed to load rooms/assets', err);
    }
  };

  // Filtered and sorted plans
  const filteredPlans = useMemo(() => {
    let result = plans;
    if (fSearch) {
      const q = fSearch.toLowerCase();
      result = result.filter(p => p.name.toLowerCase().includes(q) || p.branch_name?.toLowerCase().includes(q));
    }
    return plansSort.sortData(result);
  }, [plans, fSearch, plansSort]);

  // Filtered and sorted runs
  const filteredRuns = useMemo(() => {
    let result = runs;
    if (filterRunStatus !== 'all') {
      result = result.filter(r => r.status === filterRunStatus);
    }
    if (filterBranch !== 'all') {
      result = result.filter(r => r.branch_id === filterBranch);
    }
    return runsSort.sortData(result);
  }, [runs, filterRunStatus, filterBranch, runsSort]);

  const openCreateDialog = () => {
    setEditingPlan(null);
    setFormData({
      branch_id: branches[0]?.id || '',
      name: '',
      target_type: 'room',
      room_id: '',
      asset_id: '',
      frequency_type: 'weekly',
      interval: 1,
      cron_expression: '',
      checklist_items: [],
      is_active: true,
    });
    if (branches[0]?.id) loadRoomsAssets(branches[0].id);
    setDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setFormData({
      branch_id: plan.branch_id,
      name: plan.name,
      target_type: plan.target_type,
      room_id: plan.room_id || '',
      asset_id: plan.asset_id || '',
      frequency_type: plan.frequency_type,
      interval: plan.interval || 1,
      cron_expression: plan.cron_expression || '',
      checklist_items: plan.checklist_items || [],
      is_active: plan.is_active,
    });
    loadRoomsAssets(plan.branch_id);
    setDialogOpen(true);
  };

  const handleBranchChange = (branchId) => {
    setFormData({ ...formData, branch_id: branchId, room_id: '', asset_id: '' });
    loadRoomsAssets(branchId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        interval: formData.interval ? parseInt(formData.interval) : null,
        room_id: formData.target_type === 'room' ? (formData.room_id === 'none' ? null : formData.room_id || null) : null,
        asset_id: formData.target_type === 'asset' ? (formData.asset_id === 'none' ? null : formData.asset_id || null) : null,
        cron_expression: formData.frequency_type === 'customCron' ? formData.cron_expression : null,
      };

      if (editingPlan) {
        await api.put(`/pm/plans/${editingPlan.id}`, payload);
        toast.success(t('toast.saved'));
      } else {
        await api.post('/pm/plans', payload);
        toast.success(t('toast.saved'));
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('toast.error'));
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    try {
      await api.delete(`/pm/plans/${planId}`);
      toast.success(t('pm.planDeactivated'));
      loadData();
    } catch (err) {
      toast.error(t('toast.error'));
    }
  };

  const getFrequencyLabel = (plan) => {
    if (plan.frequency_type === 'customCron') return plan.cron_expression;
    const count = plan.interval || 1;
    if (plan.frequency_type === 'daily') return t('pm.everyDay', { count });
    if (plan.frequency_type === 'weekly') return t('pm.everyWeek', { count });
    if (plan.frequency_type === 'monthly') return t('pm.everyMonth', { count });
    return plan.frequency_type;
  };

  const clearFilters = () => {
    setFilterBranch('all');
    setFilterActive('all');
    setFilterTargetType('all');
    setFilterRunStatus('all');
    setFSearch('');
  };

  const applyFilterPreset = (filters) => {
    if (filters.branch) setFilterBranch(filters.branch);
    if (filters.active) setFilterActive(filters.active);
    if (filters.targetType) setFilterTargetType(filters.targetType);
    if (filters.runStatus) setFilterRunStatus(filters.runStatus);
    if (filters.tab) setActiveTab(filters.tab);
    toast.success(t('filters.applied'));
  };

  const hasActiveFilters = filterBranch !== 'all' || filterActive !== 'all' || filterTargetType !== 'all' || filterRunStatus !== 'all' || fSearch;

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <div data-testid="pm-plans-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{t('pm.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('pm.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SavedFiltersDropdown
            savedFilters={savedFilters}
            onSave={saveFilterPreset}
            onApply={applyFilterPreset}
            onDelete={deleteFilterPreset}
            currentFilters={{ branch: filterBranch, active: filterActive, targetType: filterTargetType, runStatus: filterRunStatus, tab: activeTab }}
            t={t}
          />
          <Button onClick={openCreateDialog} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="create-pm-plan-btn">
            <Plus className="w-4 h-4 me-2" /> {t('pm.createPlan')}
          </Button>
        </div>
      </div>

      {/* Compliance Summary Cards */}
      {compliance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{compliance.total_scheduled}</p>
                  <p className="text-xs text-muted-foreground">{t('pm.totalScheduled')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{compliance.generated}</p>
                  <p className="text-xs text-muted-foreground">{t('pm.wosGenerated')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{compliance.closed}</p>
                  <p className="text-xs text-muted-foreground">{t('status.CLOSED')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{compliance.compliance_rate}%</p>
                  <p className="text-xs text-muted-foreground">{t('pm.complianceRate')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="plans" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <Settings2 className="w-4 h-4 me-2" /> {t('pm.plans')}
            <Badge variant="secondary" className="ms-2">{filteredPlans.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="runs" className="data-[state=active]:bg-card data-[state=active]:text-brand">
            <List className="w-4 h-4 me-2" /> {t('pm.runs')}
            <Badge variant="secondary" className="ms-2">{filteredRuns.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* PM Plans Tab */}
        <TabsContent value="plans" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('common.search')}
                value={fSearch}
                onChange={(e) => setFSearch(e.target.value)}
                className="ps-9"
                data-testid="pm-search"
              />
              {fSearch && (
                <button onClick={() => setFSearch('')} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="pm-filter-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger className="w-full sm:w-[130px]" data-testid="pm-filter-active"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="active">{t('common.active')}</SelectItem>
                <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTargetType} onValueChange={setFilterTargetType}>
              <SelectTrigger className="w-full sm:w-[130px]" data-testid="pm-filter-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="room">{t('common.room')}</SelectItem>
                <SelectItem value="asset">{t('common.asset')}</SelectItem>
              </SelectContent>
            </Select>
            <TableSettingsDropdown
              columns={plansColumns}
              lockedColumns={['name', 'branch', 'status']}
              density={plansSettings.density}
              onToggleColumn={plansSettings.toggleColumn}
              onSetDensity={plansSettings.setDensity}
              onResetAll={plansSettings.resetAll}
              isVisible={plansSettings.isVisible}
              isLocked={plansSettings.isLocked}
              t={t}
            />
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
              </Button>
            )}
          </div>

          {/* Plans Table */}
          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    {plansSettings.isVisible('name') && (
                      <SortableHeader field="name" currentField={plansSort.sortField} currentDir={plansSort.sortDir} onSort={plansSort.toggleSort} className={plansSettings.densityClasses.header}>
                        {t('pm.planName')}
                      </SortableHeader>
                    )}
                    {plansSettings.isVisible('branch') && (
                      <SortableHeader field="branch_name" currentField={plansSort.sortField} currentDir={plansSort.sortDir} onSort={plansSort.toggleSort} className={plansSettings.densityClasses.header}>
                        {t('common.branch')}
                      </SortableHeader>
                    )}
                    {plansSettings.isVisible('target') && (
                      <TableHeader className={plansSettings.densityClasses.header}>{t('pm.targetType')}</TableHeader>
                    )}
                    {plansSettings.isVisible('frequency') && (
                      <TableHeader className={plansSettings.densityClasses.header}>{t('pm.frequency')}</TableHeader>
                    )}
                    {plansSettings.isVisible('nextDue') && (
                      <SortableHeader field="next_due" currentField={plansSort.sortField} currentDir={plansSort.sortDir} onSort={plansSort.toggleSort} className={plansSettings.densityClasses.header}>
                        {t('pm.nextDue')}
                      </SortableHeader>
                    )}
                    {plansSettings.isVisible('status') && (
                      <SortableHeader field="is_active" currentField={plansSort.sortField} currentDir={plansSort.sortDir} onSort={plansSort.toggleSort} className={plansSettings.densityClasses.header}>
                        {t('common.status')}
                      </SortableHeader>
                    )}
                    {plansSettings.isVisible('updatedAt') && (
                      <SortableHeader field="updated_at" currentField={plansSort.sortField} currentDir={plansSort.sortDir} onSort={plansSort.toggleSort} className={plansSettings.densityClasses.header}>
                        {t('common.updatedAt')}
                      </SortableHeader>
                    )}
                    {plansSettings.isVisible('actions') && (
                      <TableHeader align="end" className={plansSettings.densityClasses.header}>{t('common.actions')}</TableHeader>
                    )}
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {filteredPlans.length === 0 && (
                    <TableEmptyState icon={Calendar} title={t('pm.noPlans')} colSpan={plansSettings.visibleColumns.length} />
                  )}
                  {filteredPlans.map(plan => (
                    <EnhancedTableRow key={plan.id} data-testid={`pm-plan-row-${plan.id}`}>
                      {plansSettings.isVisible('name') && (
                        <EnhancedTableCell className={plansSettings.densityClasses.cell}>
                          <div>
                            <p className="font-medium text-foreground">{plan.name}</p>
                            <p className="text-xs text-muted-foreground">{plan.checklist_items?.length || 0} {t('pm.checklistItems').toLowerCase()}</p>
                          </div>
                        </EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('branch') && (
                        <EnhancedTableCell className={`text-muted-foreground ${plansSettings.densityClasses.cell}`}>{plan.branch_name}</EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('target') && (
                        <EnhancedTableCell className={plansSettings.densityClasses.cell}>
                          <div>
                            <Badge variant="outline" className="text-xs capitalize">
                              {plan.target_type === 'room' ? t('common.room') : t('common.asset')}
                            </Badge>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {plan.target_type === 'room' ? plan.room_name : plan.asset_name}
                            </p>
                          </div>
                        </EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('frequency') && (
                        <EnhancedTableCell className={`text-muted-foreground ${plansSettings.densityClasses.cell}`}>{getFrequencyLabel(plan)}</EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('nextDue') && (
                        <EnhancedTableCell className={`text-muted-foreground ${plansSettings.densityClasses.cell}`}>{formatDate(plan.next_due)}</EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('status') && (
                        <EnhancedTableCell className={plansSettings.densityClasses.cell}>
                          <Badge variant={plan.is_active ? 'default' : 'secondary'} className={plan.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : ''}>
                            {plan.is_active ? t('common.active') : t('common.inactive')}
                          </Badge>
                        </EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('updatedAt') && (
                        <EnhancedTableCell className={`text-xs text-muted-foreground ${plansSettings.densityClasses.cell}`}>{formatDate(plan.updated_at)}</EnhancedTableCell>
                      )}
                      {plansSettings.isVisible('actions') && (
                        <EnhancedTableCell align="end" className={plansSettings.densityClasses.cell}>
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(plan)} data-testid={`edit-pm-plan-${plan.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(plan.id)} className="text-red-500 hover:text-red-600" data-testid={`delete-pm-plan-${plan.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </EnhancedTableCell>
                      )}
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>

          {/* Next Scheduled Preview */}
          {plans.length > 0 && plans[0].next_dates && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-barlow">{t('pm.upcomingSchedule')}</CardTitle>
                <CardDescription>{t('pm.next5Dates')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {plans.filter(p => p.is_active).slice(0, 3).map(plan => (
                    <div key={plan.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-foreground">{plan.name}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {plan.next_dates?.slice(0, 5).map((date, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {formatDate(date)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* PM Runs Tab */}
        <TabsContent value="runs" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={filterBranch} onValueChange={setFilterBranch}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="runs-filter-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')} {t('common.branches')}</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterRunStatus} onValueChange={setFilterRunStatus}>
              <SelectTrigger className="w-full sm:w-[150px]" data-testid="runs-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                {PM_RUN_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <TableSettingsDropdown
              columns={runsColumns}
              lockedColumns={['scheduledFor', 'status', 'branch']}
              density={runsSettings.density}
              onToggleColumn={runsSettings.toggleColumn}
              onSetDensity={runsSettings.setDensity}
              onResetAll={runsSettings.resetAll}
              isVisible={runsSettings.isVisible}
              isLocked={runsSettings.isLocked}
              t={t}
            />
            {(filterBranch !== 'all' || filterRunStatus !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterBranch('all'); setFilterRunStatus('all'); }} className="text-muted-foreground">
                <X className="w-4 h-4 me-1" /> {t('filters.resetFilters')}
              </Button>
            )}
          </div>

          <Card className="bg-card border-border shadow-sm">
            <CardContent className="p-0">
              <EnhancedTable stickyHeader>
                <StickyTableHeader>
                  <tr className="border-b border-border">
                    {runsSettings.isVisible('planName') && (
                      <SortableHeader field="plan_name" currentField={runsSort.sortField} currentDir={runsSort.sortDir} onSort={runsSort.toggleSort} className={runsSettings.densityClasses.header}>
                        {t('pm.planName')}
                      </SortableHeader>
                    )}
                    {runsSettings.isVisible('branch') && (
                      <SortableHeader field="branch_name" currentField={runsSort.sortField} currentDir={runsSort.sortDir} onSort={runsSort.toggleSort} className={runsSettings.densityClasses.header}>
                        {t('common.branch')}
                      </SortableHeader>
                    )}
                    {runsSettings.isVisible('scheduledFor') && (
                      <SortableHeader field="scheduled_for" currentField={runsSort.sortField} currentDir={runsSort.sortDir} onSort={runsSort.toggleSort} className={runsSettings.densityClasses.header}>
                        {t('pm.scheduledFor')}
                      </SortableHeader>
                    )}
                    {runsSettings.isVisible('status') && (
                      <SortableHeader field="status" currentField={runsSort.sortField} currentDir={runsSort.sortDir} onSort={runsSort.toggleSort} className={runsSettings.densityClasses.header}>
                        {t('common.status')}
                      </SortableHeader>
                    )}
                    {runsSettings.isVisible('workOrder') && (
                      <TableHeader className={runsSettings.densityClasses.header}>{t('workOrder.title')}</TableHeader>
                    )}
                    {runsSettings.isVisible('woStatus') && (
                      <TableHeader className={runsSettings.densityClasses.header}>{t('pm.workOrderStatus')}</TableHeader>
                    )}
                  </tr>
                </StickyTableHeader>
                <EnhancedTableBody>
                  {filteredRuns.length === 0 && (
                    <TableEmptyState icon={List} title={t('pm.noRuns')} colSpan={runsSettings.visibleColumns.length} />
                  )}
                  {filteredRuns.map(run => (
                    <EnhancedTableRow key={run.id} data-testid={`pm-run-row-${run.id}`}>
                      {runsSettings.isVisible('planName') && (
                        <EnhancedTableCell className={`font-medium text-foreground ${runsSettings.densityClasses.cell}`}>{run.plan_name}</EnhancedTableCell>
                      )}
                      {runsSettings.isVisible('branch') && (
                        <EnhancedTableCell className={`text-muted-foreground ${runsSettings.densityClasses.cell}`}>{run.branch_name}</EnhancedTableCell>
                      )}
                      {runsSettings.isVisible('scheduledFor') && (
                        <EnhancedTableCell className={`text-muted-foreground ${runsSettings.densityClasses.cell}`}>{formatDate(run.scheduled_for)}</EnhancedTableCell>
                      )}
                      {runsSettings.isVisible('status') && (
                        <EnhancedTableCell className={runsSettings.densityClasses.cell}>
                          <Badge variant={run.status === 'generated' ? 'default' : run.status === 'failed' ? 'destructive' : 'secondary'}
                                 className={run.status === 'generated' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 
                                            run.status === 'failed' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : ''}>
                            {run.status}
                          </Badge>
                        </EnhancedTableCell>
                      )}
                      {runsSettings.isVisible('workOrder') && (
                        <EnhancedTableCell className={runsSettings.densityClasses.cell}>
                          {run.generated_work_order_id ? (
                            <Button variant="link" className="p-0 h-auto text-blue-600" onClick={() => navigate(`/work-orders/${run.generated_work_order_id}`)}>
                              #{run.generated_work_order_id.slice(0, 8)}
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </EnhancedTableCell>
                      )}
                      {runsSettings.isVisible('woStatus') && (
                        <EnhancedTableCell className={runsSettings.densityClasses.cell}>
                          {run.work_order_status ? (
                            <Badge variant="outline">{t(`status.${run.work_order_status}`, run.work_order_status)}</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </EnhancedTableCell>
                      )}
                    </EnhancedTableRow>
                  ))}
                </EnhancedTableBody>
              </EnhancedTable>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? t('pm.editPlan') : t('pm.createPlan')}</DialogTitle>
            <DialogDescription>
              {editingPlan ? t('pm.subtitle') : t('pm.subtitle')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pm.planName')} *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('pm.planNamePlaceholder')}
                  required
                  data-testid="pm-plan-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.branch')} *</Label>
                <Select value={formData.branch_id} onValueChange={handleBranchChange} required>
                  <SelectTrigger data-testid="pm-plan-branch-select"><SelectValue placeholder={t('workRequest.selectBranch')} /></SelectTrigger>
                  <SelectContent>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pm.targetType')}</Label>
                <Select value={formData.target_type} onValueChange={(v) => setFormData({ ...formData, target_type: v, room_id: '', asset_id: '' })}>
                  <SelectTrigger data-testid="pm-plan-target-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="room">{t('common.room')}</SelectItem>
                    <SelectItem value="asset">{t('common.asset')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{formData.target_type === 'room' ? t('common.room') : t('common.asset')}</Label>
                {formData.target_type === 'room' ? (
                  <Select value={formData.room_id || 'none'} onValueChange={(v) => setFormData({ ...formData, room_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="pm-plan-room-select"><SelectValue placeholder={t('workRequest.selectRoom')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('common.none')}</SelectItem>
                      {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={formData.asset_id || 'none'} onValueChange={(v) => setFormData({ ...formData, asset_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="pm-plan-asset-select"><SelectValue placeholder={t('workRequest.selectAsset')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('common.none')}</SelectItem>
                      {assets.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pm.frequency')}</Label>
                <Select value={formData.frequency_type} onValueChange={(v) => setFormData({ ...formData, frequency_type: v })}>
                  <SelectTrigger data-testid="pm-plan-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('pm.daily')}</SelectItem>
                    <SelectItem value="weekly">{t('pm.weekly')}</SelectItem>
                    <SelectItem value="monthly">{t('pm.monthly')}</SelectItem>
                    <SelectItem value="customCron">{t('pm.customCron')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.frequency_type !== 'customCron' ? (
                <div className="space-y-2">
                  <Label>{t('pm.interval')}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.interval}
                    onChange={(e) => setFormData({ ...formData, interval: e.target.value })}
                    data-testid="pm-plan-interval-input"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>{t('pm.cronExpression')}</Label>
                  <Input
                    value={formData.cron_expression}
                    onChange={(e) => setFormData({ ...formData, cron_expression: e.target.value })}
                    placeholder="0 9 * * 1"
                    data-testid="pm-plan-cron-input"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="pm-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="pm-active">{t('pm.planIsActive')}</Label>
            </div>

            <Separator />

            <ChecklistBuilder
              items={formData.checklist_items}
              onChange={(items) => setFormData({ ...formData, checklist_items: items })}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="pm-plan-submit-btn">
                {editingPlan ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
